import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  MessageSquare,
  Inbox,
  LifeBuoy,
  Mail as MailIcon,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/DataTable";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import {
  ApiError,
  createTicket,
  listTickets,
  type TicketAttachment,
  type TicketPriority,
  type TicketStatus,
  type TicketSummary,
} from "@/lib/api";
import { formatRelative } from "@/lib/format";

const STATUS_FILTERS: Array<{ value: TicketStatus | "all"; label: string }> = [
  { value: "all", label: "Tous" },
  { value: "open", label: "Ouverts" },
  { value: "in_progress", label: "En cours" },
  { value: "resolved", label: "Résolus" },
  { value: "closed", label: "Fermés" },
];

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Basse",
  medium: "Moyenne",
  high: "Haute",
  urgent: "Urgente",
};

const PRIORITY_COLORS: Record<TicketPriority, "info" | "success" | "warning" | "danger"> = {
  low: "info",
  medium: "success",
  high: "warning",
  urgent: "danger",
};

const STATUS_COLORS: Record<TicketStatus, "info" | "success" | "warning" | "danger"> = {
  open: "info",
  in_progress: "warning",
  resolved: "success",
  closed: "success",
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Ouvert",
  in_progress: "En cours",
  resolved: "Résolu",
  closed: "Fermé",
};

export function SupportListPage() {
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");
  const [page, setPage] = useState(1);
  const [newOpen, setNewOpen] = useState(false);

  const query = useQuery({
    queryKey: ["tickets", "list", statusFilter, page],
    queryFn: () =>
      listTickets({
        status: statusFilter,
        page,
        per_page: 25,
      }),
  });

  const isUnavailable =
    query.error instanceof ApiError && query.error.status === 503;
  const isPermissionIssue =
    query.error instanceof ApiError &&
    query.error.status === 400 &&
    query.error.detail.includes("admin users must provide");

  return (
    <div>
      <PageHeader
        title="Support"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Support" }]}
        actions={
          !isUnavailable && !isPermissionIssue ? (
            <button onClick={() => setNewOpen(true)} className="btn btn-primary btn-sm">
              <Plus size={14} />
              Nouveau ticket
            </button>
          ) : undefined
        }
      />

      {isUnavailable && <UnavailableCard detail={query.error?.message} />}
      {isPermissionIssue && (
        <div className="card">
          <div className="card-body text-sm text-[#7e8299]">
            En tant qu'admin Moko, utilise le backoffice pour consulter les tickets
            de tous les marchands.
          </div>
        </div>
      )}

      {!isUnavailable && !isPermissionIssue && (
        <>
          <div className="card mb-5">
            <div className="card-body flex items-center gap-1 py-3 overflow-x-auto">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => {
                    setStatusFilter(f.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors shrink-0 ${
                    statusFilter === f.value
                      ? "bg-brand-50 text-brand-700"
                      : "text-[#5e6278] hover:bg-[#f5f8fa]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            {query.isLoading && (
              <div className="card-body text-center text-sm text-[#7e8299]">Chargement…</div>
            )}
            {!query.isLoading &&
              !query.error &&
              query.data?.data.length === 0 && <EmptyState onNew={() => setNewOpen(true)} />}
            {!query.isLoading && query.data && query.data.data.length > 0 && (
              <div>
                {query.data.data.map((t) => (
                  <TicketRow key={t.id} ticket={t} />
                ))}
              </div>
            )}

            {query.data && query.data.pagination.total_pages > 1 && (
              <div className="px-5 py-4 border-t border-[#eff2f5] flex items-center justify-between">
                <div className="text-xs text-[#7e8299]">
                  Page {query.data.pagination.page} / {query.data.pagination.total_pages}
                  {" · "}
                  {query.data.pagination.total} tickets
                </div>
                <div className="flex gap-1">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="btn btn-light btn-sm"
                  >
                    Précédent
                  </button>
                  <button
                    disabled={page >= query.data.pagination.total_pages}
                    onClick={() => setPage((p) => p + 1)}
                    className="btn btn-light btn-sm"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <NewTicketModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TicketRow({ ticket }: { ticket: TicketSummary }) {
  return (
    <Link
      to={`/support/${ticket.ticket_id}`}
      className={`block px-5 py-4 border-b border-[#f5f8fa] hover:bg-[#f9fafb] transition-colors ${
        ticket.unread_by_merchant ? "bg-brand-50/30" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
          <MessageSquare size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`text-sm text-[#181c32] ${ticket.unread_by_merchant ? "font-bold" : "font-medium"} truncate`}>
                {ticket.subject}
              </div>
              {ticket.unread_by_merchant && (
                <div className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
              )}
            </div>
            <div className="text-[11px] text-[#a1a5b7] shrink-0">
              {formatRelative(ticket.last_message_at || ticket.created_at)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-[#7e8299] font-mono">{ticket.ticket_id}</span>
            <Badge color={STATUS_COLORS[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
            <Badge color={PRIORITY_COLORS[ticket.priority]}>{PRIORITY_LABELS[ticket.priority]}</Badge>
            <span className="text-[11px] text-[#7e8299]">{ticket.category}</span>
            <span className="text-[11px] text-[#7e8299]">·</span>
            <span className="text-[11px] text-[#7e8299] inline-flex items-center gap-1">
              <MessageSquare size={11} />
              {ticket.message_count}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="px-5 py-16 text-center">
      <Inbox size={40} className="mx-auto text-[#cbd5e0] mb-3" />
      <div className="font-bold text-[#181c32] mb-1">Aucun ticket pour l'instant</div>
      <div className="text-sm text-[#7e8299] mb-4">
        Contactez l'équipe Moko en ouvrant un ticket. Nous vous répondrons dès que possible.
      </div>
      <button onClick={onNew} className="btn btn-primary btn-sm">
        <Plus size={14} />
        Ouvrir un ticket
      </button>
    </div>
  );
}

function UnavailableCard({ detail }: { detail?: string }) {
  return (
    <div className="card">
      <div className="card-body text-center py-14">
        <LifeBuoy size={40} className="mx-auto text-[#cbd5e0] mb-3" />
        <div className="font-bold text-[#181c32] mb-2">Support non activé pour ce marchand</div>
        <div className="text-sm text-[#7e8299] mb-5 max-w-md mx-auto leading-relaxed">
          {detail ||
            "Le module ticketing n'est pas encore provisionné pour votre compte."}{" "}
          Contactez l'équipe Moko par email pour toute demande.
        </div>
        <a
          href="mailto:support@gofreshbakery.com"
          className="btn btn-primary btn-sm"
        >
          <MailIcon size={14} />
          support@gofreshbakery.com
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function NewTicketModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [category, setCategory] = useState("Technical");
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createTicket({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        category,
        attachments: attachments.length > 0 ? attachments : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      reset();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError(err instanceof Error ? err.message : "Erreur");
    },
  });

  const reset = () => {
    setSubject("");
    setDescription("");
    setPriority("medium");
    setCategory("Technical");
    setAttachments([]);
    setError(null);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError("Sujet et description obligatoires");
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Nouveau ticket support"
      subtitle="L'équipe Moko sera notifiée et vous répondra ici"
      size="lg"
      closeOnOverlay={!mutation.isPending}
      footer={
        <>
          <button onClick={handleClose} disabled={mutation.isPending} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button
            type="submit"
            form="new-ticket-form"
            disabled={mutation.isPending}
            className="btn btn-primary btn-sm"
          >
            {mutation.isPending ? "Envoi…" : "Envoyer le ticket"}
          </button>
        </>
      }
    >
      <form id="new-ticket-form" onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="form-label">Sujet</label>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="form-input"
            placeholder="Ex : Callback non reçu sur paiement pay_..."
            maxLength={255}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Catégorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="form-input"
            >
              <option value="Technical">Technique</option>
              <option value="Billing">Facturation</option>
              <option value="Refund">Remboursement</option>
              <option value="Onboarding">Onboarding</option>
              <option value="Other">Autre</option>
            </select>
          </div>
          <div>
            <label className="form-label">Priorité</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              className="form-input"
            >
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
        </div>
        <div>
          <label className="form-label">Description</label>
          <textarea
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-input min-h-[140px] resize-y"
            placeholder="Décrivez le problème, les étapes pour le reproduire, les IDs de transactions concernées…"
          />
        </div>
        <div>
          <label className="form-label">Pièces jointes (optionnel)</label>
          <AttachmentPicker
            value={attachments}
            onChange={setAttachments}
            disabled={mutation.isPending}
          />
        </div>
        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </form>
    </Modal>
  );
}
